import { z } from 'zod';
import { lenientString, lenientUuid, lenientDateString, lenientOptionalNode, lenientNodeArray } from '../schema-extensions';
import { ProtectionContactNode } from './protection-contact-node-schema';
import { ProtectionPriceNode } from './protection-price-node-schema';
import { ProtectionReferenceNode } from './protection-reference-node-schema';

// ============================================================================
// CONSTANTS
// ============================================================================
const PROTECTION_CONSTANTS = {
  PROVIDER_MAX_LENGTH: 100,
  POLICY_MAX_LENGTH: 100,
  DATE_FORMAT_LENGTH: 10,
  NOTES_MAX_LENGTH: 1000,
} as const;

// ============================================================================
// LENIENT SCHEMA (for deserialization)
// ============================================================================
const lenientProtectionNodeSchema = z.object({
  id: lenientUuid(),
  provider: lenientString(PROTECTION_CONSTANTS.PROVIDER_MAX_LENGTH),
  policy: lenientString(PROTECTION_CONSTANTS.POLICY_MAX_LENGTH),
  purchaseDate: lenientDateString(''),
  coverStartDate: lenientDateString(''),
  noClaimStartDate: lenientDateString('').optional(),
  payments: lenientNodeArray(ProtectionPriceNode.deserialize),
  targets: lenientNodeArray(ProtectionReferenceNode.deserialize),
  contact: lenientOptionalNode(ProtectionContactNode),
  notes: lenientString(PROTECTION_CONSTANTS.NOTES_MAX_LENGTH),
}).transform(val => ({
  id: val.id,
  provider: val.provider,
  policy: val.policy,
  purchaseDate: val.purchaseDate,
  coverStartDate: val.coverStartDate,
  noClaimStartDate: val.noClaimStartDate,
  payments: val.payments,
  targets: val.targets,
  contact: val.contact,
  notes: val.notes,
}));

// ============================================================================
// STRICT SCHEMA (for validation)
// ============================================================================
const strictProtectionNodeSchema = z.object({
  id: z.string().min(1, 'ID is required'),
  provider: z.string()
    .min(1, 'Provider is required')
    .max(PROTECTION_CONSTANTS.PROVIDER_MAX_LENGTH, `Provider must be ${PROTECTION_CONSTANTS.PROVIDER_MAX_LENGTH} characters or less`),
  policy: z.string()
    .min(1, 'Policy is required')
    .max(PROTECTION_CONSTANTS.POLICY_MAX_LENGTH, `Policy must be ${PROTECTION_CONSTANTS.POLICY_MAX_LENGTH} characters or less`),
  purchaseDate: z.string()
    .length(PROTECTION_CONSTANTS.DATE_FORMAT_LENGTH, 'Purchase date must be in YYYY-MM-DD format')
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Purchase date must be in YYYY-MM-DD format'),
  coverStartDate: z.string()
    .length(PROTECTION_CONSTANTS.DATE_FORMAT_LENGTH, 'Cover start date must be in YYYY-MM-DD format')
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Cover start date must be in YYYY-MM-DD format'),
  noClaimStartDate: z.string()
    .length(PROTECTION_CONSTANTS.DATE_FORMAT_LENGTH, 'No claim start date must be in YYYY-MM-DD format')
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'No claim start date must be in YYYY-MM-DD format')
    .optional(),
  payments: z.array(z.unknown()).min(1, 'At least one payment is required').transform(arr => arr.map(val => ProtectionPriceNode.deserialize(val))),
  targets: z.array(z.unknown()).min(1, 'At least one target is required').transform(arr => arr.map(val => ProtectionReferenceNode.deserialize(val))),
  contact: z.unknown().transform(val => ProtectionContactNode.create(val)),
  notes: z.string()
    .max(PROTECTION_CONSTANTS.NOTES_MAX_LENGTH, `Notes must be ${PROTECTION_CONSTANTS.NOTES_MAX_LENGTH} characters or less`)
    .optional()
    .transform(val => val ?? ''),
}).transform(val => ({
  id: val.id,
  provider: val.provider,
  policy: val.policy,
  purchaseDate: val.purchaseDate,
  coverStartDate: val.coverStartDate,
  noClaimStartDate: val.noClaimStartDate,
  payments: val.payments,
  targets: val.targets,
  contact: val.contact,
  notes: val.notes,
}));

// ============================================================================
// TYPE
// ============================================================================
export type ProtectionNode = z.infer<typeof lenientProtectionNodeSchema>;

// ============================================================================
// NAMESPACE (for Constants and Methods)
// ============================================================================
export namespace ProtectionNode {
  export const Constants = PROTECTION_CONSTANTS;

  export function deserialize(data: unknown): ProtectionNode {
    return lenientProtectionNodeSchema.parse(data);
  }

  export function create(data: unknown): ProtectionNode {
    return strictProtectionNodeSchema.parse(data);
  }

  /**
   * Get the display name for a protection policy
   */
  export function getDisplayName(node: ProtectionNode): string {
    return `${node.provider} - ${node.policy}`;
  }

  /**
   * Check if the protection policy has an active no-claims period
   */
  export function hasNoClaimsPeriod(node: ProtectionNode): boolean {
    return node.noClaimStartDate !== undefined && node.noClaimStartDate !== '';
  }

  /**
   * Get all protected targets of a specific type
   */
  export function getTargetsByType(node: ProtectionNode, type: string): ProtectionReferenceNode[] {
    return node.targets.filter(target => target.protectionType === type);
  }

  /**
   * Get the most recent payment
   */
  export function getCurrentPayment(node: ProtectionNode): ProtectionPriceNode | undefined {
    if (node.payments.length === 0) return undefined;
    return node.payments[node.payments.length - 1];
  }

  export function isActive(node: ProtectionNode, referenceDate: Date = new Date()): boolean {
    const payment = ProtectionNode.getCurrentPayment(node);
    return !payment?.coverEndDate || new Date(payment.coverEndDate) >= referenceDate;
  }
}
